import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { STATUS_COLORS } from "utils/customizedTheme"
import SelectField from "components/atoms/fields/SelectField"
import { useGET_academicPeriodsSelectQuery } from "redux/api/academicPeriodsApi"
import { Form, Formik } from "formik"
import { useGET_kpiEvaluationsQuery } from "redux/api/kpiEvaluationsApi"
import { ArrowRightAlt, Create } from "@mui/icons-material"
import InputField from "components/atoms/fields/InputField"
import { Button, Switch } from "@mui/material"
import CheckboxField from "components/atoms/fields/CheckboxField"
import SubmitButton from "components/atoms/buttons/SubmitButton"
import { usePOST_resetStudentProgramMutation, usePATCH_studentProgramMutation } from "redux/api/programsApi"
import { useErrorAlert } from "utils/customHooks/useErrorAlert"
import { useSuccessAlert } from "utils/customHooks/useSuccessAlert"
import { RootState } from "redux/store"
import { useSelector } from "react-redux"
import { useGET_setupOptionsQuery } from "redux/api/setupApi"


interface IProps {
  student: any
  subject: any
}

const ProgramStudentPlanning: FC<IProps> = ({student, subject}) => {
  const { t } = useTranslation()

  const [editAccommodations, setEditAccommodations] = useState(false)
  const [editModifications, setEditModifications] = useState(false)
  const [editStrategies, setEditStrategies] = useState(false)
  const [editPath, setEditPath] = useState(false)

  const { data: setupData } = useGET_setupOptionsQuery()

  const [saveProgram, {isLoading: isLoadingSave, isSuccess: isSuccessSave, error: errorSave }] = usePATCH_studentProgramMutation()
  const [resetProgram, {isLoading: isLoadingReset, isSuccess: isSuccessReset, error: errorReset }] = usePOST_resetStudentProgramMutation()

  const isLoading = isLoadingSave || isLoadingReset
  const isSuccess = isSuccessSave || isSuccessReset
  const error = errorSave || errorReset

  useErrorAlert(error)
  useSuccessAlert(isSuccess ? t('alerts.items-saved-success') : '')

  const user = useSelector((state: RootState) => state['user'].user)

  return (
    <>
    {student.program && setupData && <Formik 
         initialValues={{  
          is_active: student.program.is_active,
          accommodations: student.program.accommodations,
          modifications: student.program.modifications,
          instructional_strategies: student.program.instructional_strategies,
          steps: student.program.steps,
         }} 
         validationSchema={null} 
         onSubmit={(values)=>{
            saveProgram({student_id: student.student_profile.id, subject_id: subject.id, body: values})
         }}>
         <Form>
          <div className="info-row">
              <div className="info-box">
                  <div className="info-box-title">{t('common.target-performance')}</div>
                  <div className="info-box-content">
                      <div className="stats-bar">
                          <div style={{ display: "flex" }}>
                            <div className="stats-bar-fill" style={{ 
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                                textAlign: 'right',
                                padding: 5,
                                width: `${student.baseline_score}%`,
                                background: 'orange'
                            }}>
                              {student.baseline_score}%
                            </div>
                            <div className="stats-bar-fill" style={{ 
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                textAlign: 'right',
                                padding: 5,
                                width: `${student.program?.target_performance - student.baseline_score}%`,
                                background: '#9effc2'
                              }}>{student.program?.target_performance}%</div>
                          </div>

                          <div className="stats-bar-line" style={{ left: '50%' }}></div>
                          <div className="stats-bar-line" style={{ left: '80%' }}></div>
                      </div>
                      <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: '5px' }}>
                          <div style={{ width: '50% '}}>{t('zpd-tags.gap')}</div>
                          <div style={{ width: '30%' }}>{t('zpd-tags.bridge')}</div>
                          <div>{t('zpd-tags.strength')}</div>
                      </div>

                  </div>  
              </div>
              <div className="info-box">
                  <div className="info-box-title">{t('common.target-risk')}</div>
                  <div className="info-box-content">
                      <div className="stats-bar">     
                          <div style={{ display: "flex" }}>
                            <div className="stats-bar-fill" style={{ 
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                                textAlign: 'right',
                                padding: 5,
                                width: `${student.program?.target_risk}%`,
                                background: 'orange'
                            }}>
                              {student.program?.target_risk}%
                            </div>
                            
                            <div className="stats-bar-fill" style={{ 
                                borderTopLeftRadius: 0,
                                borderBottomLeftRadius: 0,
                                textAlign: 'right',
                                padding: 5,
                                width: `${student.risk - student.program?.target_risk}%`,
                                background: 'red'
                              }}>{student.risk}%</div>
                          </div>
                          <div className="stats-bar-line" style={{ left: '50%' }}></div>
                          <div className="stats-bar-line" style={{ left: '80%' }}></div>
                      </div> 
                      <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: '5px' }}>
                              <div style={{ width: '50% '}}>Low risk</div>
                              <div style={{ width: '30%' }}>Moderate risk</div>
                              <div>High risk</div>
                      </div>        
                  </div>
              </div>
          </div>

          <div className="info-row">

              {setupData.filter(sd => sd.slug == "programs_accommodations" && sd.is_enabled).length == 1 &&                 
              <div className="info-box">
                  <div className="info-box-title">
                    <div style={{ display: 'flex', width: '100%' }}>
                          <div style={{ flex: 1 }}>{t('common.accommodations')}</div>
                          {user?.role != "parent" &&  <div style={{ cursor: 'pointer' }} onClick={() => {
                            setEditAccommodations(!editAccommodations)
                          }}>
                            <Create />
                          </div>}
                    </div>   
                  </div>
                  <div className="info-box-content" style={{ minHeight: 200 }}>
                      {editAccommodations && <InputField multiline={true} hideClear fieldProps={{ label: '', name: 'accommodations' }} />}
                      {!editAccommodations && student.program?.accommodations.split("\n").map((item, idx) => <div key={idx}>
                        &bull; {item}
                      </div>)}
                  </div>
              </div>}

              {setupData.filter(sd => sd.slug == "programs_modifications" && sd.is_enabled).length == 1 &&             
              <div className="info-box">
                  <div className="info-box-title">
                      <div style={{ display: 'flex', width: '100%' }}>
                          <div style={{ flex: 1 }}>{t('common.modifications')}</div>
                          {user?.role != "parent" && <div style={{ cursor: 'pointer' }} onClick={() => {
                            setEditModifications(!editModifications)
                          }}>
                            <Create />
                          </div>}
                      </div>
                  </div>
                  <div className="info-box-content" style={{ minHeight: 200 }}>
                      {editModifications && <InputField multiline={true} hideClear fieldProps={{ label: '', name: 'modifications' }} />}

                      {!editModifications && student.program?.modifications.split("\n").map((item, idx) => <div key={idx}>
                        &bull; {item}
                      </div>)}
                  </div>
              </div>}

              {setupData.filter(sd => sd.slug == "programs_strategies" && sd.is_enabled).length == 1 &&               
              <div className="info-box">
                  <div className="info-box-title">
                    <div style={{ display: 'flex', width: '100%' }}>
                          <div style={{ flex: 1 }}>{t('common.instructional-strategies')}</div>
                          
                          {user?.role != "parent" && <div style={{ cursor: 'pointer' }} onClick={() => {
                            setEditStrategies(!editStrategies)
                          }}>
                            <Create />
                          </div>}
                    </div> 
                  </div>
                  <div className="info-box-content" style={{ minHeight: 200 }}>
                      {editStrategies && <InputField multiline={true} hideClear fieldProps={{ label: '', name: 'instructional_strategies' }} />}  

                      {!editStrategies && student.program?.instructional_strategies.split("\n").map((item, idx) => <div key={idx}>
                        &bull; {item}
                      </div>)}
                  </div>
              </div>}
          </div>


          {setupData.filter(sd => sd.slug == "programs_steps" && sd.is_enabled).length == 1 &&                   
          <div className="info-row">
              <div className="info-box">
                  <div className="info-box-title">
                       <div style={{ display: 'flex', width: '100%' }}>
                          <div style={{ flex: 1 }}>{t('common.dynamic-learning-path')}</div>
                          
                          {user?.role != "parent" && <div style={{ cursor: 'pointer' }} onClick={() => {
                            setEditPath(!editPath)
                          }}>
                            <Create />
                          </div>}
                    </div> 
                  </div>
                  <div className="info-box-content">

                      {editPath && <InputField multiline={true} hideClear fieldProps={{ label: '', name: 'steps' }} />}

                      
                      {!editPath && <div style={{ display: "flex", alignItems: "center", gap: 20 }}>

                        {student.program?.steps.split("\n").map((item, idx) => <div key={idx} style={{ display: "flex", alignItems: "center" }}>
                          
                          {idx > 0 && <div style={{ paddingRight: 20 }}>
                              <ArrowRightAlt style={{ height: 60 }} />
                            </div>}

                            <div key={idx} style={{
                              borderRadius: 10,
                              backgroundColor: "#ffc39aff",
                              padding: 10,
                              fontSize: 16
                            }}>
                              {idx+1}. {item}
                            </div>

                        </div>)}
                    </div> } 

                  </div>
              </div>
          </div>
          }

          {user?.role != "parent" &&                 
          <div style={{ display: 'flex', width: '100%', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <Button
                  variant='contained'
                  color='white'
                  onClick={() => {
                    resetProgram({student_id: student.student_profile.id, subject_id: subject.id})
                  }}
                >
                  {`${t('common.reset')}`}
                </Button>
              </div>
              <div>
                <CheckboxField 
                    isSwitch={true}
                    fieldProps={{
                      label: t('common.active'),
                      name: 'is_active'
                    }} 
                  />
              </div>
              <div>
                <SubmitButton
                    text={`${t('common.save')}`}
                    isLoading={isLoading}
                />
              </div>
          </div>
          }   

        </Form>
      </Formik> }                     
    </>
  )
}

export default ProgramStudentPlanning